import React, { useState } from "react";
import { ChevronDown, ChevronsDownUp, ChevronsUpDown, Plus } from "lucide-react";
import Countdown from "../bond-wheel/Countdown";
import {
  formatCasinoCoin,
  getCasinoTablePnl,
  isCasinoCreator,
  LIVE_CASINO_STATUSES,
} from "../../utils/casinoHelpers";
import BondSpinTablePnl, { BondSpinPnlStamp } from "../bond-spin/BondSpinTablePnl";

const BondSpinGameRow = ({
  game,
  active,
  expanded,
  hasPendingBet = false,
  isMine = false,
  onSelect,
  onToggleExpand,
}) => {
  const isLive = LIVE_CASINO_STATUSES.includes(game.status);
  const isCompleted = game.status === "Completed";
  const isPast = !isLive;
  const bettors = Number(game.bettorsCount ?? 0);
  const pot = Number(game.allocatedAmount ?? 0);
  const pnl = getCasinoTablePnl(game);

  let statusClass = "live";
  let statusLabel = "Live";
  if (isCompleted) {
    statusClass = "settled";
    statusLabel = "Done";
  } else if (isPast) {
    statusClass = "cancelled";
    statusLabel = game.status;
  }

  return (
    <div
      className={`bplay-game-item ${active ? "active" : ""} ${expanded ? "expanded" : ""} ${isPast ? "past" : ""}${pnl.available ? ` bspin-item-${pnl.tone}` : ""}`}
    >
      <div className="bplay-game-row-shell">
        <button type="button" className="bplay-game-row" onClick={() => onSelect?.(game)}>
          <div className="bplay-game-row-top">
            <span className="bplay-game-row-host">{game.creator?.name || "Host"}</span>
            <span className={`bplay-game-row-status ${statusClass}`}>{statusLabel}</span>
            {hasPendingBet ? <span className="bplay-game-row-pending">Your bet</span> : null}
          </div>
          <div className="bplay-game-row-pot-line">
            <div
              className={`bplay-game-row-pot bplay-game-row-pot-coin${pnl.available ? ` bspin-pot-${pnl.tone}` : ""}`}
            >
              {formatCasinoCoin(pot)} COIN
            </div>
            <BondSpinPnlStamp pnl={pnl} />
          </div>
          {pnl.available ? <BondSpinTablePnl pnl={pnl} isMine={isMine} compact /> : null}
          <div className="bplay-game-row-meta">
            <span>{bettors} bets</span>
            {isPast ? (
              <span className="bplay-game-row-result">Finished</span>
            ) : (
              <Countdown deadline={game.deadline} tone="live" />
            )}
          </div>
        </button>

        <button
          type="button"
          className="bplay-game-expand"
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse spin details" : "Expand spin details"}
          onClick={() => onToggleExpand?.(game)}
        >
          <ChevronDown size={14} />
        </button>
      </div>

      {expanded ? (
        <div className="bplay-game-row-detail">
          <div className="bplay-game-row-stats">
            <div>
              <span>Prize pool</span>
              <strong>{formatCasinoCoin(pot)} COIN</strong>
            </div>
            <div>
              <span>Opened</span>
              <strong>{formatCasinoCoin(pnl.opened || pot)} COIN</strong>
            </div>
            <div>
              <span>Bets</span>
              <strong>{bettors}</strong>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const BondSpinGameRail = ({
  games = [],
  selectedGameId,
  pendingBetGameIds = null,
  onSelectGame,
  onCreate,
  loading = false,
  userId = null,
}) => {
  const [listExpanded, setListExpanded] = useState(false);
  const [expandedGameId, setExpandedGameId] = useState(null);

  const liveGames = games.filter((g) => LIVE_CASINO_STATUSES.includes(g.status));
  const pastGames = games.filter((g) => !LIVE_CASINO_STATUSES.includes(g.status));

  const handleSelect = (game) => {
    onSelectGame?.(game);
    setExpandedGameId(String(game._id));
  };

  const handleToggleExpand = (game) => {
    const id = String(game._id);
    setExpandedGameId((prev) => (prev === id ? null : id));
    if (expandedGameId !== id) onSelectGame?.(game);
  };

  return (
    <aside
      className={`bplay-games-rail bplay-games-rail-spin ${listExpanded ? "is-expanded" : "is-collapsed"}`}
      aria-label="Bond spins"
    >
      <div className="bplay-games-rail-head">
        <span className="bplay-games-rail-label">Spins</span>
        <div className="bplay-games-head-actions">
          <button
            type="button"
            className="bplay-games-expand"
            onClick={() => setListExpanded((v) => !v)}
            title={listExpanded ? "Collapse list" : "Expand list"}
            aria-expanded={listExpanded}
          >
            {listExpanded ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
          </button>
          <button type="button" className="bplay-games-create bplay-games-create-spin" onClick={onCreate} title="Create spin">
            <Plus size={14} />
          </button>
        </div>
      </div>

      <div className="bplay-games-list">
        {loading && games.length === 0 ? (
          <p className="bplay-games-empty">Loading…</p>
        ) : games.length === 0 ? (
          <p className="bplay-games-empty">No live spins yet.</p>
        ) : (
          <>
            {liveGames.length > 0 ? (
              <>
                <p className="bplay-games-section-label">Live</p>
                {liveGames.map((game) => (
                  <BondSpinGameRow
                    key={String(game._id)}
                    game={game}
                    active={String(game._id) === String(selectedGameId)}
                    expanded={String(game._id) === String(expandedGameId)}
                    hasPendingBet={pendingBetGameIds?.has(String(game._id))}
                    isMine={isCasinoCreator(game, userId)}
                    onSelect={handleSelect}
                    onToggleExpand={handleToggleExpand}
                  />
                ))}
              </>
            ) : null}
            {pastGames.length > 0 ? (
              <>
                <p className="bplay-games-section-label">Past</p>
                {pastGames.map((game) => (
                  <BondSpinGameRow
                    key={String(game._id)}
                    game={game}
                    active={String(game._id) === String(selectedGameId)}
                    expanded={String(game._id) === String(expandedGameId)}
                    isMine={isCasinoCreator(game, userId)}
                    onSelect={handleSelect}
                    onToggleExpand={handleToggleExpand}
                  />
                ))}
              </>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
};

export default BondSpinGameRail;
